import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "../axios";
import getConfig from "../config";
import toast from "../hooks/toast";
import {
    buildApiV3Url,
    buildApiV3UrlWithQuery,
    fixSearchStringForCertificates,
    formatAddress,
} from "../shared/Utils";
import type { RootState } from "./types";

// -----------------------------------------------------------------------------
// CERTCAP-71685 — Bill-to/Ship-to report (calls the CERTCAP-71684 API).
// GET /api/v3/companies/{companyId}/customers/bill-to-ship-to (OData: $filter/$top/$skip).
// The API returns bill-to customers with a comma-separated `shipToRegions` string and a flat
// `shipToCustomers` array (address as components); the UI formats addresses and groups ship-to
// customers by state on its side. Sorting is intentionally not supported (the regions column is
// computed server-side and cannot be ordered).
// -----------------------------------------------------------------------------

export interface ShipToCustomer {
    id: string;
    name: string;
    code: string;
    address: string;
    state: string;
}

export interface BillToCustomer {
    id: string;
    name: string;
    code: string;
    address: string;
    regions: string;
    shipCustomers: ShipToCustomer[];
}

export interface PaginateState {
    totalRecords: number;
    startIndex: number;
    endIndex: number;
    rowsPerPage: number;
}

export interface PageState {
    data: BillToCustomer[];
    paginate: PaginateState;
}

export interface BillToShipToFormState {
    customerName: string;
    customerCode: string;
    // State ids. billToRegion filters on the bill-to customer's own region (its address state);
    // shipToRegion filters on the ship-to region (ship customers' states / configured ship-to states).
    billToRegion: string[];
    shipToRegion: string[];
}

export interface BillToShipToReportState {
    isLoading: boolean;
    page: PageState;
    formState: BillToShipToFormState;
    // [apiFieldName, ascending] — the field is an API property name (see SORTABLE_API_FIELDS) and
    // is sent to $orderBy verbatim. Only bill-to columns are sortable; "Ship-To Regions" is
    // computed server-side and cannot be ordered.
    sortColumn: [string, boolean];
    isExportDialogOpen: boolean;
    isExporting: boolean;
}

// The set of $orderBy-able fields, named exactly as the API's view-model PROPERTIES
// (BillToShipToListViewModel) — which is also what the grid columns use as their sort id, so the
// sortColumn value is sent to the API verbatim with no aliasing. SafeFetch validates $orderBy
// against these property names, not the entity columns CustomFilter's aliasName points at. Only
// Bill-to Name and Code are sortable. "Ship-To Regions" is a computed server-side string with no
// backing property, and "Bill-To Address" is a display join of several fields (ordering by any one
// backing column, e.g. the often-empty AddressLine1, would be misleading) — both stay non-sortable.
const SORTABLE_API_FIELDS = new Set(["BillToCustomerName", "BillToCustomerCode"]);

// Shapes of the API response (camelCase JSON from the .NET Page<BillToShipToListViewModel>).
// Mirrors the CERTCAP-71684 view models: value types are required, nullable value types and
// reference types are `| null`. Note ShipToCustomerStateId is [JsonIgnore] server-side (filter
// only), so it is intentionally absent from the response shape.
interface ApiCustomerState {
    id: number;
    initials: string | null;
    name: string | null;
    geoCode: string | null;
}
interface ApiCustomerCountry {
    id: number;
    name: string | null;
    initials: string | null;
    abbreviation: string | null;
}
interface ApiShipToCustomer {
    shipCustomerId: number;
    shipStateId: number | null;
    shipState: string | null;
    shipCustomerCode: string | null;
    shipCustomerName: string | null;
    shipCustomerAddress: string | null;
    shipCustomerAddressLine2: string | null;
    shipCustomerCity: string | null;
    shipCustomerZip: string | null;
    shipCustomerCountry: string | null;
}
interface ApiBillToShipTo {
    billToCustomerId: number;
    billToCustomerName: string | null;
    billToCustomerCode: string | null;
    billToCustomerAddressLine1: string | null;
    billToCustomerAddressLine2: string | null;
    billToCustomerCity: string | null;
    billToCustomerZip: string | null;
    billToCustomerStateId: number | null;
    billToCustomerState: ApiCustomerState | null;
    billToCustomerCountryId: number | null;
    billToCustomerCountry: ApiCustomerCountry | null;
    shipToRegions: string | null;
    shipToRegionsIds: number[] | null;
    shipToCustomers: ApiShipToCustomer[] | null;
}
interface ApiPage {
    value?: ApiBillToShipTo[];
    count?: number;
}

const emptyPage: PageState = {
    data: [],
    paginate: {
        totalRecords: 0,
        startIndex: 0,
        endIndex: 0,
        rowsPerPage: 0,
    },
};

const initialState: BillToShipToReportState = {
    isLoading: false,
    page: { ...emptyPage },
    formState: {
        customerName: "",
        customerCode: "",
        billToRegion: [],
        shipToRegion: [],
    },
    // Default sort is by customer code (ascending) to match the legacy CertCapture report — some
    // customers have no name, so code is the more reliable default ordering.
    sortColumn: ["BillToCustomerCode", true],
    isExportDialogOpen: false,
    isExporting: false,
};

const [showToast, hideToast] = toast();

// =============================================================================
// SELECTORS
// =============================================================================

export const selectIsLoading = (state: RootState): boolean => state.billToShipToReport.isLoading;
export const selectPage = (state: RootState): PageState => state.billToShipToReport.page;
export const selectFormState = (state: RootState): BillToShipToFormState =>
    state.billToShipToReport.formState;
export const selectSortColumn = (state: RootState): [string, boolean] =>
    state.billToShipToReport.sortColumn;
export const selectIsExportDialogOpen = (state: RootState): boolean =>
    state.billToShipToReport.isExportDialogOpen;
export const selectIsExporting = (state: RootState): boolean =>
    state.billToShipToReport.isExporting;

// =============================================================================
// REQUEST/RESPONSE HELPERS
// =============================================================================

// Builds the $filter for the report using AvaTax REST filtering (infix operators, not OData
// functions — see https://developer.avalara.com/avatax/filtering-in-rest/). Fields are the
// view-model PROPERTY names (same identifiers as $orderBy). Name/Code use `contains` for
// substring search; the two region filters use `in` over state ids (BillToCustomerStateId for the
// bill-to's own region, ShipToCustomerStateId for the ship-to region). Single quotes in the
// search text are escaped with the shared fixSearchStringForCertificates util.
export const buildBillToShipToFilter = (formState: BillToShipToFormState): string => {
    const clauses: string[] = [];
    if (formState.customerName?.trim()) {
        clauses.push(
            `BillToCustomerName contains '${fixSearchStringForCertificates(formState.customerName.trim())}'`
        );
    }
    if (formState.customerCode?.trim()) {
        clauses.push(
            `BillToCustomerCode contains '${fixSearchStringForCertificates(formState.customerCode.trim())}'`
        );
    }
    if (formState.billToRegion?.length) {
        clauses.push(`BillToCustomerStateId in (${formState.billToRegion.join(",")})`);
    }
    if (formState.shipToRegion?.length) {
        clauses.push(`ShipToCustomerStateId in (${formState.shipToRegion.join(",")})`);
    }
    return clauses.join(" and ");
};

// Builds the OData $orderBy for the report (e.g. "BillToCustomerName asc"). sortColumn[0] is the
// API field name (see SORTABLE_API_FIELDS); returns "" for non-sortable columns (e.g. regions).
export const buildBillToShipToOrderBy = (sortColumn: [string, boolean]): string => {
    const [field, ascending] = sortColumn;
    if (!SORTABLE_API_FIELDS.has(field)) {
        return "";
    }
    return `${field} ${ascending ? "asc" : "desc"}`;
};

const toShipToCustomer = (s: ApiShipToCustomer): ShipToCustomer => ({
    id: String(s.shipCustomerId),
    name: s.shipCustomerName ?? "",
    code: s.shipCustomerCode ?? "",
    address: formatAddress(
        [s.shipCustomerAddress, s.shipCustomerAddressLine2, s.shipCustomerCity, s.shipState],
        { initials: s.shipCustomerCountry },
        s.shipCustomerZip
    ) as string,
    state: s.shipState ?? "",
});

const toBillToCustomer = (item: ApiBillToShipTo): BillToCustomer => ({
    id: String(item.billToCustomerId),
    name: item.billToCustomerName ?? "",
    code: item.billToCustomerCode ?? "",
    address: formatAddress(
        [
            item.billToCustomerAddressLine1,
            item.billToCustomerAddressLine2,
            item.billToCustomerCity,
            item.billToCustomerState?.initials,
        ],
        { initials: item.billToCustomerCountry?.initials },
        item.billToCustomerZip
    ) as string,
    regions: item.shipToRegions ?? "",
    shipCustomers: (item.shipToCustomers ?? []).map(toShipToCustomer),
});

// =============================================================================
// THUNK
// =============================================================================

interface FetchBillToShipToArgs {
    pageSize?: number;
    pageNumber?: number;
}

// A superseded (aborted) request rejects with "aborted" so the reducer can leave the newer
// request's state untouched; a genuine failure rejects with "error".
type FetchReject = "aborted" | "error";

// Tracks the in-flight request so a new fetch cancels the previous one (latest wins) rather than
// dropping the newer request. Module-scoped — only one report grid is mounted at a time.
let inFlightController: AbortController | null = null;

export const fetchBillToShipToReport = createAsyncThunk<
    PageState,
    FetchBillToShipToArgs | undefined,
    { state: RootState; rejectValue: FetchReject }
>("billToShipToReport/fetch", async (args, { getState, rejectWithValue }) => {
    const { pageSize, pageNumber } = args ?? {};
    const { formState, sortColumn } = getState().billToShipToReport;
    const { activeCompany, rowsPerPage } = getState().session;

    // Cancel any request still in flight; the newest fetch wins.
    inFlightController?.abort();
    const controller = new AbortController();
    inFlightController = controller;

    // Nullish-coalesce so an explicit 0 from a caller is respected (|| would treat it as "unset").
    const finalPageSize = pageSize ?? rowsPerPage ?? 20;
    const finalPageNumber = pageNumber ?? 1;
    const startIndex = (finalPageNumber - 1) * finalPageSize;

    try {
        const url = buildApiV3UrlWithQuery(
            `companies/${activeCompany.id}/customers/bill-to-ship-to`,
            buildBillToShipToFilter(formState),
            "",
            finalPageSize,
            startIndex,
            buildBillToShipToOrderBy(sortColumn)
        );

        const { data: apiPage } = await axios.get<ApiPage>(url, {
            withCredentials: true,
            signal: controller.signal,
        });

        const data = (apiPage.value ?? []).map(toBillToCustomer);
        return {
            data,
            paginate: {
                totalRecords: apiPage.count ?? 0,
                startIndex,
                endIndex: startIndex + data.length,
                rowsPerPage: finalPageSize,
            },
        };
    } catch {
        // Superseded by a newer fetch — let that request own the loading/page state.
        if (controller.signal.aborted) {
            return rejectWithValue("aborted");
        }
        showToast("error", "Failed to fetch Bill-to/Ship-to report.");
        return rejectWithValue("error");
    }
});

// =============================================================================
// EXPORT
// =============================================================================

// Maps the grid's sort column id (an API field name — see SORTABLE_API_FIELDS) to the export
// worker's allowed sort fields (ECMBillToShipToReport in certcapture/core accepts
// bill_customer_number | bill_customer_name | bill_state_initials | ship_to_state). Address is not
// an orderable export column, so it falls back to the default (bill_customer_number).
const EXPORT_ORDER_BY_FIELDS: Record<string, string> = {
    BillToCustomerName: "bill_customer_name",
    BillToCustomerCode: "bill_customer_number",
};

// Builds the export request body (BillToShipToReportExportModel). Keys are camelCase; the .NET
// endpoint deserializes case-insensitively and re-serializes as PascalCase into the export Criteria
// the core worker reads. User/locker/client fields are populated server-side.
export const buildBillToShipToExportPayload = (
    formState: BillToShipToFormState,
    sortColumn: [string, boolean],
    title: string
): Record<string, unknown> => ({
    title,
    billToCustomerName: formState.customerName?.trim() || "",
    billToCustomerNumber: formState.customerCode?.trim() || "",
    billToCustomerState: (formState.billToRegion ?? []).map(Number),
    shipToCustomerState: (formState.shipToRegion ?? []).map(Number),
    orderBy: EXPORT_ORDER_BY_FIELDS[sortColumn[0]] ?? "bill_customer_number",
    orderByDirection: sortColumn[1] ? "ASC" : "DESC",
});

// POSTs the export request (mirrors the Missing Certificates export). Fulfils on a clean HTTP 200
// — the reducer then closes the dialog and points the user to their DocumentLocker downloads.
// Anything else (non-200 or a thrown error) surfaces an error toast and rejects, leaving the dialog
// open so the user can retry. isExporting is driven by the thunk lifecycle in extraReducers;
// `condition` drops a second submit while one is already in flight.
export const postBillToShipToExportAPI = createAsyncThunk<
    void,
    string,
    { state: RootState; rejectValue: "error" }
>(
    "billToShipToReport/export",
    async (title, { getState, rejectWithValue }) => {
        const { formState, sortColumn } = getState().billToShipToReport;
        const payload = buildBillToShipToExportPayload(formState, sortColumn, title);

        try {
            const { status } = await axios.post(
                buildApiV3Url("export-bill-to-ship-to-report"),
                payload,
                { withCredentials: true }
            );

            if (status !== 200) {
                showToast(
                    "error",
                    "Failed to export Bill-to/Ship-to report. Please try again later."
                );
                return rejectWithValue("error");
            }

            const downloadsUrl = `https://${getConfig().hostnames.docLockerClient}/mylockers/downloads`;
            window.open(downloadsUrl, "_blank", "noopener,noreferrer");
            showToast(
                "success",
                "We are working on your export. When it's ready, find it in",
                "bill-to-ship-to-export-success",
                true,
                "your downloads",
                () => {
                    hideToast("bill-to-ship-to-export-success");
                    window.open(downloadsUrl, "_blank", "noopener,noreferrer");
                },
                "The time it takes to export data depends on the size of the file and network traffic."
            );
            return undefined;
        } catch {
            showToast("error", "Failed to export Bill-to/Ship-to report. Please try again later.");
            return rejectWithValue("error");
        }
    },
    { condition: (_title, { getState }) => !getState().billToShipToReport.isExporting }
);

// =============================================================================
// REDUCERS
// =============================================================================

const billToShipToReportSlice = createSlice({
    name: "billToShipToReport",
    initialState,
    reducers: {
        setPageToNull: state => {
            state.page = { ...emptyPage };
        },
        setFormField: (
            state,
            action: PayloadAction<{ field: keyof BillToShipToFormState; value: string | string[] }>
        ) => {
            const { field, value } = action.payload;
            state.formState = { ...state.formState, [field]: value };
        },
        setSortColumn: (state, action: PayloadAction<[string, boolean]>) => {
            state.sortColumn = action.payload;
        },
        setIsExportDialogOpen: (state, action: PayloadAction<boolean>) => {
            state.isExportDialogOpen = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchBillToShipToReport.pending, state => {
                state.isLoading = true;
            })
            .addCase(fetchBillToShipToReport.fulfilled, (state, action) => {
                state.isLoading = false;
                state.page = action.payload;
            })
            .addCase(fetchBillToShipToReport.rejected, (state, action) => {
                // Aborted = superseded by a newer in-flight fetch; leave state to that request.
                if (action.payload === "aborted") {
                    return;
                }
                state.isLoading = false;
                state.page = { ...emptyPage };
            })
            .addCase(postBillToShipToExportAPI.pending, state => {
                state.isExporting = true;
            })
            .addCase(postBillToShipToExportAPI.fulfilled, state => {
                // Only a clean HTTP 200 fulfils; non-200 rejects. Close the dialog on success.
                state.isExporting = false;
                state.isExportDialogOpen = false;
            })
            .addCase(postBillToShipToExportAPI.rejected, state => {
                // Only genuine failures reach here — a submit dropped by `condition` (another
                // export already in flight) is not dispatched, so the running export keeps its flag.
                state.isExporting = false;
            });
    },
});

export const { setPageToNull, setFormField, setSortColumn, setIsExportDialogOpen } =
    billToShipToReportSlice.actions;

export default billToShipToReportSlice.reducer;
